import React, { useState } from 'react';

import { TEACHERS_STUDENTS_DETAIL } from 'config/urls';

import { ReactComponent as ArrowDownIcon } from 'pages/Teachers/StudentDetail/assets/arrow-down.svg';
import { ReactComponent as StandardIcon } from 'pages/Teachers/StudentDetail/assets/standard.svg';
import PerformanceGraph from 'pages/Teachers/StudentDetail/components/PerformanceGraph';
import { useEdLightTracking } from 'utils/integrations/mixpanel';
import {
  formatDateString,
  getProgressColorClass,
  getStatusColor
} from 'utils/students/goals';

import styles from './styles.module.scss';

const GoalRow = ({
  title,
  description,
  standards = [],
  date,
  progress,
  targetAccuracy,
  hasGraphData,
  submissionDetails,
  setSubmissionPeriodFilter,
  goalId,
  sectionId,
  studentId
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const { trackEvent } = useEdLightTracking({
    feature: 'goals',
    view: 'student_goals_detail',
    customRouteConstant: TEACHERS_STUDENTS_DETAIL
  });

  const handleRowToggle = () => {
    if (!hasGraphData) {
      return;
    }

    setIsExpanded((prev) => !prev);

    trackEvent('goal_expanded', {
      goal_id: goalId || null,
      student_id: studentId
    });
  };

  const statusColor = getStatusColor({
    progress: Number(progress),
    targetAccuracy: targetAccuracy
      ? Number(String(targetAccuracy).replace('%', ''))
      : 100
  });
  const progressColorClass = getProgressColorClass({
    color: statusColor,
    styleModule: styles
  });

  const rowWrapperClassName = `${styles.rowWrapper} ${
    isExpanded ? styles.expanded : ''
  }`;
  const goalRowClassName = `${styles.goalRow} ${
    hasGraphData ? styles.isClickable : styles.isDisabled
  }`;
  const arrowWrapperClassName = `${styles.arrowWrapper} ${
    isExpanded ? styles.arrowRotate : ''
  }`;
  const progressFillClassName = `${styles.progressFill} ${progressColorClass}`;

  const tabIndexValue = hasGraphData ? 0 : -1;
  const progressWidthStyle = { width: `${progress}%` };

  const renderedStandards = standards?.map((standard, index) => (
    <span key={index} className={styles.tag}>
      <StandardIcon />
      {standard.code}
    </span>
  ));

  return (
    <div className={rowWrapperClassName}>
      <div
        className={goalRowClassName}
        onClick={handleRowToggle}
        role="button"
        tabIndex={tabIndexValue}
      >
        <div className={styles.colGoal}>
          {title && <div className={styles.goalTitle}>{title}</div>}
          <div className={styles.goalDescription}>{description}</div>

          <div className={styles.tagsContainer}>{renderedStandards}</div>
        </div>

        <div className={styles.colDate}>
          <span className={styles.dateText}>
            {formatDateString({ isoString: date }) || '-'}
          </span>
        </div>

        <div className={styles.colProgress}>
          <div className={styles.progressContainer}>
            {typeof progress === 'number' ? (
              <div className={styles.progressBarWrapper}>
                <div
                  className={progressFillClassName}
                  style={progressWidthStyle}
                />
              </div>
            ) : (
              '-'
            )}
            <span className={styles.progressLabel}>
              {typeof progress === 'number' ? `${progress}` : ''}
            </span>
            {hasGraphData && (
              <div className={arrowWrapperClassName}>
                <ArrowDownIcon />
              </div>
            )}
          </div>
        </div>
      </div>

      {isExpanded && (
        <PerformanceGraph
          goalId={goalId}
          studentId={studentId}
          sectionId={sectionId}
          isExpanded={isExpanded}
          submissionDetails={submissionDetails}
          setSubmissionPeriodFilter={setSubmissionPeriodFilter}
          trackEvent={trackEvent}
        />
      )}
    </div>
  );
};

export default GoalRow;
